import { type AssetData, AssetImageFormat } from "./mediaLibrary/asset.interface";

export type ResponsiveValue<Type> = {
	all?: Type,
	sm?: Type,
	md?: Type,
	lg?: Type,
	xl?: Type,
	"2xl"?: Type
}

export enum ModeType {
	all = "all",
	sm = "sm",
	md = "md",
	lg = "lg",
	xl = "xl",
	'2xl' = "2xl",
	'w-full' = "w-full",
}

export type DimensionsInterface = {
	marginTop?: ResponsiveValue<number|string>,
	marginBottom?: ResponsiveValue<number|string>,
	marginLeft?: ResponsiveValue<number|string>,
	marginRight?: ResponsiveValue<number|string>,

	paddingTop?: ResponsiveValue<number|string>,
	paddingBottom?: ResponsiveValue<number|string>,
	paddingLeft?: ResponsiveValue<number|string>,
	paddingRight?: ResponsiveValue<number|string>,
}

export type CommonSettings = DimensionsInterface & {
	id?: string;
	class?: string;
	textAlign?: ResponsiveValue<string>;
	textColor: string;
	textClass: string;

	backgroundColor?: string;
	backgroundClass?: string;
	backgroundRepeat?: string;
	backgroundPosition?: string;
	backgroundSize?: string;

	backgroundImageId?: number;
	backgroundImageWidth?: number;
	backgroundImageHeight?: number;
	backgroundImageCrop?: boolean;
	backgroundImageFormat?: AssetImageFormat;
	backgroundImageQuality?: number;
}

export enum ItemType {
	TEXT = 'text',
	TEXT_ANCHOR = 'text_anchor',
	CODE = 'code',
	FAQ = 'faq',
	REUSE = 'reuse',
	SLIDER = 'slider',
	CONTACT = 'contact',
	MAP = 'map',
	NEWS = 'news',
	SERVICE = 'service',
	CONTAINER = 'container',
	VIMEO = 'vimeo',
}

export type PageItemSliderData = {
	sliderAutoplay?: boolean;
	sliderNavigation?: boolean;
	sliderPager?: boolean;
	sliderLoop?: boolean;
	sliderDelay?: number;
	sliderCenteredSlides?: boolean;
	sliderSlidesPerView?: ResponsiveValue<number>;
	sliderSpaceBetween?: ResponsiveValue<number>;
	sliderDimensions?: DimensionsInterface;
}

export type PageItemVideoData = {
	vimeoId: string;

	vimeoAutoplay?: boolean;
	vimeoLoop?: boolean;
	vimeoMute: boolean;

	vimeoColor: string;

	vimeoShowTitle: boolean;
	vimeoShowByline: boolean;
	vimeoShowPortrait: boolean;
	vimeoShowControls: boolean;
}

export type PageItemData = CommonSettings & PageItemSliderData & PageItemVideoData & {
	type: ItemType;
	content: string|number|PageItemData[];
	size?: ResponsiveValue<string>;
	order?: ResponsiveValue<number>;

	wrapper: string;
	title?: string;
	href?: string;
	editorState?: string;
}

export type PageRenderedItemData = PageItemData & {
	page?: PageData;
	latestNews?: PageData[];
	testimonials?: PageData[];
	rendered?: string;
	backgroundImage?: AssetData;
}

export enum SectionType {
	NORMAL = 'normal',
	REUSE = 'reuse',
}

export type PageSectionData = CommonSettings & {
	type: SectionType;
	alignItems: ResponsiveValue<string>;
	justifyContent: ResponsiveValue<string>;
	items: PageItemData[];
	reusable?: number;
	gap?: ResponsiveValue<number>;
	maxWidth: string;
	bleedBackgroundColor: boolean;
	bleedBackgroundImage: boolean;
	vimeoId: string;
}
export type PageRenderedSectionData = PageSectionData & {
	page?: PageData;
	backgroundImage?: AssetData;
}

export type PersonData = {
	id?: number,
	email: string,
	firstname: string,
	surname: string,
	name?: string,
	registrationDate: string,
	optInFlag: boolean,
	logonCount: number,
	lastLogonDate: string,
	active: boolean,
}

export type ObjData = {
	id?: number,
	ownerId?: number,
	owner?: PersonData
	creationDate?: string
}

export type MinimumListData = {
	id?: number,
	name?: string,
}

export type PageCategoryData = MinimumListData & {
	name: string,
	templateDefault?: string
	urlPrefix?: string,
	isHomepage?: boolean,
	isSeo?: boolean,
	order?: number,
}

export type PageData = {
	id?: number,
	obj?: ObjData,
	category?: PageCategoryData,
	order?: number,
	featured?: boolean,
	coverId?: number,
	cover?: AssetData
	headerId?: number,
	header?: AssetData
	modificationDate?: string,
	url?: string,
	absoluteUrl?: string,
	title: string,
	htmlTitle?: string,
	blurb: string
	metaKeywords?: string;
	metaDescription?: string,
	sitemapFreqId?: number;
	sitemapFreq?: string;
	sitemapPriorityId?: number;
	sitemapPriority?: string;
	noindex?: boolean,
	content: PageSectionData[],
	published: boolean,
}

export type PageErrors = {
	objId?: boolean|string,
	ownerId?: boolean|string,
	categoryId?: boolean|string,
	order?: boolean|string,
	featured?: boolean|string,
	coverId?: boolean|string,
	headerId?: boolean|string,
	url?: boolean|string,
	title?: boolean|string,
	htmlTitle?: boolean|string,
	blurb?: boolean|string,
	metaKeywords?: boolean|string;
	metaDescription?: boolean|string,
	sitemapFreqId?: boolean|string;
	sitemapPriorityId?: boolean|string;
	noindex?: boolean|string,
	content?: boolean|string,
	published?: boolean|string,
	css?: boolean|string
	creationDate?: boolean|string;
}

export type MenuItemData = {
	id?: number,
	title: string,
	nodeId: number,
	menuId: number,
	typeId: number,
	type: string,
	parentId: number,
	productId?: number,
	categoryId?: number,
	pageId?: number,
	page?: PageData,
	url: string,
}

export type MenuData = {
	id?: number,
	name: string,
	url: string,
	maxDepth?: number,
	canDelete?: boolean
	items: MenuItemData[]
}

export type ProductData = {
	id?: number,
	obj?: ObjData,
	category?: ProductCategoryData,
	coverId?: number,
	cover?: AssetData,
	brochureId?: number,
	brochure?: AssetData,
	modificationDate?: string,
	url?: string,
	name: string,
	htmlTitle?: string,
	metaDescription?: string,
	about: string,
	features: string,
	description: string,
	published: boolean,
	available: boolean,

	sizeFrom?: number,
	sizeTo?: number,

	priceFrom?: number,
	priceTo?: number,
	underOffer?: boolean,

	city?: string,
	address?: string,
	postcode?: string,
	latitude?: string,
	longitude?: string,

	typeId?: number,
	type?: string,

	gallery?: AssetData[]
}

export type ProductCategoryData = MinimumListData & {
	id?: number,
	name: string,
	url: string,
	published: boolean,
	cover?: AssetData,
	title?: string,
	htmlTitle?: string,
	metaDescription?: string,
	content?: string
}
